import React, { useEffect, useId, useRef, useState } from 'react';
import { CREATABLE_TYPES, TYPE_GROUPS, TYPE_HINTS, typeLabel } from './sectionTypes';

/**
 * "+" trigger that opens a keyboard-operable type menu and calls
 * onAdd(type, position). Arrow keys move, Home/End jump, Escape closes and
 * returns focus to the trigger, clicking outside closes.
 */
export default function AddSectionButton({ position, onAdd, busy, prominent = false }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = `add-section-menu-${useId()}`;

  useEffect(() => {
    if (!open) return undefined;
    setActive(0);
    const first = menuRef.current?.querySelector('[role="menuitem"]');
    first?.focus();
    const onDocDown = (e) => {
      if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const items = menuRef.current?.querySelectorAll('[role="menuitem"]');
    items?.[active]?.focus();
  }, [active, open]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const choose = (type) => {
    close(false);
    onAdd(type, position);
  };

  const onMenuKeyDown = (e) => {
    const n = CREATABLE_TYPES.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((a) => (a + 1) % n);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((a) => (a - 1 + n) % n);
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(n - 1);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: prominent ? '12px 0 0' : '2px 0',
        minHeight: prominent ? undefined : 18,
      }}
    >
      {!prominent && (
        <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: '1px dashed var(--color-border)' }} />
      )}
      <button
        ref={triggerRef}
        type="button"
        className={prominent ? 'btn btn-secondary' : 'btn btn-ghost btn-sm'}
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={prominent ? undefined : `Add a section at position ${position + 1}`}
        title={prominent ? undefined : 'Add a section here'}
        style={prominent ? undefined : { position: 'relative', background: 'var(--color-surface)', padding: '0 8px', lineHeight: '18px', fontSize: 14 }}
      >
        {prominent ? '+ Add a section' : '+'}
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Section type"
          onKeyDown={onMenuKeyDown}
          className="card"
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
            marginTop: 4,
            padding: 6,
            minWidth: 300,
            maxHeight: '70vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          }}
        >
          {TYPE_GROUPS.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <span
                aria-hidden="true"
                style={{ display: 'block', padding: '6px 8px 2px', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--color-muted)' }}
              >
                {group.label}
              </span>
              {group.types.map((type) => {
                const i = CREATABLE_TYPES.indexOf(type);
                return (
                  <button
                    key={type}
                    type="button"
                    role="menuitem"
                    tabIndex={i === active ? 0 : -1}
                    className="btn btn-ghost btn-sm"
                    onClick={() => choose(type)}
                    onMouseEnter={() => setActive(i)}
                    style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 12, textAlign: 'left' }}
                  >
                    <span style={{ fontWeight: 600 }}>{typeLabel(type)}</span>
                    <span style={{ color: 'var(--color-muted)', fontWeight: 400, fontSize: 12 }}>{TYPE_HINTS[type]}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
